import {
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import './kiosk-keyboard.css';
import { logger } from '../util/logger';

export interface KioskKeyboardRef {
  setStyle: (style: KioskKeyboardStyle) => void;
}

export interface KioskKeyboardProps {
  // none
}

type InputType = HTMLInputElement | HTMLTextAreaElement;

interface RegisteredInputListener {
  eventName: string;
  listener: EventListener;
}

interface RegisteredInput {
  input: InputType;
  listeners: RegisteredInputListener[];
}

enum KeyboardLayout {
  DEFAULT = 'default',
  ALT = 'alt',
  SHIFT = 'shift',
  NUMBER = 'number',
  EMOJI = 'emoji',
}

const KEYBOARD_LAYOUTS = {
  [KeyboardLayout.DEFAULT]: [
    'q w e r t y u i o p {bksp}',
    'a s d f g h j k l {enter}',
    '{shift} z x c v b n m , . {shift}',
    '{alt} {emoji} {space} {altright} {downkeyboard}',
  ],
  [KeyboardLayout.SHIFT]: [
    'Q W E R T Y U I O P {bksp}',
    'A S D F G H J K L {enter}',
    '{shiftactivated} Z X C V B N M , . {shiftactivated}',
    '{alt} {emoji} {space} {altright} {downkeyboard}',
  ],
  [KeyboardLayout.ALT]: [
    '1 2 3 4 5 6 7 8 9 0 {bksp}',
    `@ # $ & * ( ) ' " {enter}`,
    '{shift} % - + = / ; : ! ? {shift}',
    '{default} {emoji} {space} {back} {downkeyboard}',
  ],
  [KeyboardLayout.EMOJI]: [
    '😀 😊 😅 😂 🙂 😉 😍 😛 😠 😎 {bksp}',
    `😏 😬 😭 😓 😱 😪 😬 😴 😯 {enter}`,
    '😐 😇 🤣 😘 😚 😆 😡 😥 😓 🙄 {shift}',
    '{default} {emoji} {space} {altright} {downkeyboard}',
  ],
  [KeyboardLayout.NUMBER]: ['1 2 3', '4 5 6', '7 8 9', '. 0', '{bksp}'],
};

const KEYBOARD_DISPLAY = {
  '{alt}': '.?123',
  '{emoji}': '\uD83D\uDE03',
  '{shift}': '⇧',
  '{shiftactivated}': '⇧',
  '{enter}': 'return',
  '{bksp}': '⌫',
  '{altright}': '.?123',
  '{downkeyboard}': '🞃',
  '{space}': ' ',
  '{default}': 'ABC',
  '{back}': '⇦',
};

const KIOSK_KEYBOARD_CLASS_NAME = 'kiosk-keyboard';

export interface KioskKeyboardStyle {
  width?: string;
}

const DEFAULT_KEYBOARD_STYLE: KioskKeyboardStyle = {
  width: '100%',
};

const KioskKeyboard = forwardRef<
  KioskKeyboardRef | undefined,
  KioskKeyboardProps
>((_props: KioskKeyboardProps, ref): ReactElement => {
  const keyboardRef = useRef<KeyboardReactInterface | null>(null);
  const activeInput = useRef<InputType | null>(null);

  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [keyboardLayoutName, setKeyboardLayoutName] = useState(
    KeyboardLayout.DEFAULT
  );

  const [display, setDisplay] = useState(KEYBOARD_DISPLAY);

  // store layout name in ref so we can access it in handlers without
  // it forcing a recreation of the handlers
  const keyboardLayoutNameRef = useRef<KeyboardLayout>(KeyboardLayout.DEFAULT);
  keyboardLayoutNameRef.current = keyboardLayoutName;

  const [keyboardStyle, setKeyboardStyle] = useState<KioskKeyboardStyle>(
    DEFAULT_KEYBOARD_STYLE
  );

  const showKeyboard = useCallback(() => {
    setIsKeyboardVisible(true);
  }, []);

  const hideKeyboard = useCallback(() => {
    if (activeInput.current) {
      activeInput.current.blur();
      activeInput.current = null;
    }

    if (keyboardRef.current) {
      keyboardRef.current.clearInput();
    }

    setKeyboardLayoutName(KeyboardLayout.DEFAULT);
    setIsKeyboardVisible(false);
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return {
        setStyle: (style: KioskKeyboardStyle) => {
          setKeyboardStyle(style);
        },
        setInput: (
          input: string,
          inputName?: string,
          skipSync?: boolean
        ): void | undefined => {
          keyboardRef.current?.setInput(input, inputName, skipSync);
        },
      };
    },
    []
  );

  /**
   * Effect handles hiding the keyboard when clicking outside of the keyboard
   * or the active input
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const keyboardElement = document.querySelector(
        `.${KIOSK_KEYBOARD_CLASS_NAME}`
      );

      if (
        keyboardElement &&
        !keyboardElement.contains(event.target as Node) &&
        activeInput.current &&
        !activeInput.current.contains(event.target as Node)
      ) {
        setIsKeyboardVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [hideKeyboard]);

  /**
   * Effect handles observing DOM for inputs being added / removed so we can
   * bind a focus listener which is used to toggle the keyboard visibility
   */
  useEffect(() => {
    const observers: MutationObserver[] = [];
    const registeredInputs: RegisteredInput[] = [];

    const registerInput = (input: InputType) => {
      // if the input is already registered then we skip it
      if (registeredInputs.some((r) => r.input === input)) {
        return;
      }

      const focus = () => {
        if (input.type === 'number' || input.inputMode === 'numeric') {
          setKeyboardLayoutName(KeyboardLayout.NUMBER);
        } else {
          setKeyboardLayoutName(KeyboardLayout.DEFAULT);
        }

        activeInput.current = input;

        // sync current input value to keyboard
        if (keyboardRef.current) {
          keyboardRef.current.setInput(input.value);
        }

        let enter = 'return';

        switch (input.enterKeyHint) {
          case 'done':
          case 'next':
            enter = 'done';
            break;

          default:
            break;
        }

        setDisplay({
          ...KEYBOARD_DISPLAY,
          '{enter}': enter,
        });

        showKeyboard();
      };

      const change = (evt: any) => {
        if (keyboardRef.current) {
          keyboardRef.current.setInput(evt.target.value);
        }
      };

      const registeredInput: RegisteredInput = {
        input,
        listeners: [
          { eventName: 'focus', listener: focus },
          { eventName: 'change', listener: change },
        ],
      };

      for (const { eventName, listener } of registeredInput.listeners) {
        input.addEventListener(eventName, listener);
      }

      registeredInputs.push(registeredInput);

      // if the input being added is already focused then we need to show the
      // keyboard immediately
      if (input.ownerDocument.activeElement === input) {
        focus();
      }
    };

    const unregisterInput = (input: InputType) => {
      const registeredInputIdx = registeredInputs.findIndex(
        (r) => r.input === input
      );

      if (registeredInputIdx > -1) {
        const parentDoc = input.ownerDocument;

        if (parentDoc.contains(input)) {
          logger.info(
            'skipping input removal since parent still contains input'
          );
          return;
        }

        const registeredInput = registeredInputs[registeredInputIdx];

        if (registeredInput) {
          for (const { eventName, listener } of registeredInput.listeners) {
            input.removeEventListener(eventName, listener);
          }

          // if the input we were monitoring is removed the DOM then we need
          // to hide the keyboard
          if (registeredInput.input === activeInput.current) {
            activeInput.current = null;
            hideKeyboard();
          }
        }

        registeredInputs.splice(registeredInputIdx, 1);
      }
    };

    const registerInputsInIFrames = (parent: HTMLElement) => {
      const existingIFrames = parent.querySelectorAll('iframe');

      if (existingIFrames.length > 0) {
        existingIFrames.forEach((iframe) => {
          const body = iframe.contentDocument?.body;

          if (body) {
            observe(body, 'iframe');
          }
        });
      }
    };

    const registerInputsInPage = (parent: HTMLElement) => {
      const existingInputs = parent.querySelectorAll('input, textarea');

      if (existingInputs.length > 0) {
        existingInputs.forEach((input) => {
          registerInput(input as InputType);
        });
      }
    };

    const observe = (el: HTMLElement, name = 'default') => {
      logger.info('observing inputs', {
        name,
      });

      const observer = new MutationObserver((mutationsList) => {
        const observeIFrame = (iframe: HTMLIFrameElement) => {
          const iframeBody = iframe.contentDocument?.body;

          if (iframeBody) {
            observe(iframeBody, 'iframe');
          }
        };

        for (const mutation of mutationsList) {
          // look for iframes and observe any that we find so we can traverse
          // their dom as well
          mutation.addedNodes.forEach((node) => {
            if (node.nodeName === 'IFRAME') {
              observeIFrame(node as HTMLIFrameElement);
            }

            if ('querySelectorAll' in node) {
              const nestedIframes: HTMLIFrameElement[] =
                // @ts-ignore
                node.querySelectorAll('iframe');

              for (const iframe of nestedIframes) {
                observeIFrame(iframe);
              }
            }
          });

          const inputsToAdd = getInputsFromNodeList(mutation.addedNodes);
          const inputsToRemove = getInputsFromNodeList(mutation.removedNodes);

          inputsToAdd.forEach((input) => {
            registerInput(input);
          });

          inputsToRemove.forEach((input) => {
            unregisterInput(input);
          });
        }
      });

      observer.observe(el, {
        childList: true,
        subtree: true,
      });

      observers.push(observer);
    };

    // start observing document.body for dynamic changes to the dom
    observe(document.body);

    // query for existing iframes on the page
    registerInputsInIFrames(document.body);

    // query for existing inputs on the page
    registerInputsInPage(document.body);

    return () => {
      for (const observer of observers) {
        observer.disconnect();
      }

      const clonedRegisteredInputs = [...registeredInputs];

      if (clonedRegisteredInputs) {
        clonedRegisteredInputs.forEach((registeredInput) => {
          unregisterInput(registeredInput.input);
        });
      }
    };
  }, [showKeyboard, hideKeyboard]);

  const onChange = useCallback(async (value: string) => {
    if (activeInput.current) {
      let modifiedVal = value;

      // when typing a decimal the input will show blank text if the number
      // ends with only the decimal. i.e, 1. would show blank. For the UI
      // we strip the . until the next character is typed since the keyboard
      // is tracking the actual value
      if (keyboardLayoutNameRef.current === KeyboardLayout.NUMBER) {
        if (modifiedVal.endsWith('.')) {
          modifiedVal = modifiedVal.slice(0, -1);
        }
      }

      // find the character that was changed so we can emit the appropriate key
      // on the keyboard events
      const key = findCharacterChange(modifiedVal, activeInput.current.value);

      setInputValue(activeInput.current, modifiedVal);

      const events: Event[] = [
        new KeyboardEvent('keypress', {
          bubbles: true,
          cancelable: true,
          key,
        }),
        new InputEvent('input', {
          bubbles: true,
          data: modifiedVal,
          // this is required for ionic components to update the value
          // @ts-ignore
          detail: { value: modifiedVal },
        }),
        new KeyboardEvent('keyup', {
          bubbles: true,
          cancelable: true,
          key,
        }),
      ];

      for (const event of events) {
        activeInput.current.dispatchEvent(event);
      }
    }
  }, []);

  const handleLayoutChange = useCallback(
    (button: string) => {
      const currentLayout = keyboardLayoutNameRef.current;
      let layoutName: KeyboardLayout | undefined;

      switch (button) {
        case '{shift}':
        case '{shiftactivated}':
        case '{default}':
          layoutName =
            currentLayout === KeyboardLayout.DEFAULT
              ? KeyboardLayout.SHIFT
              : KeyboardLayout.DEFAULT;
          break;

        case '{alt}':
        case '{altright}':
          layoutName =
            currentLayout === KeyboardLayout.ALT
              ? KeyboardLayout.DEFAULT
              : KeyboardLayout.ALT;
          break;

        case '{emoji}':
          layoutName =
            currentLayout === KeyboardLayout.EMOJI
              ? KeyboardLayout.DEFAULT
              : KeyboardLayout.EMOJI;
          break;

        case '{downkeyboard}':
          hideKeyboard();
          break;

        default:
          break;
      }

      if (layoutName) {
        setKeyboardLayoutName(layoutName);
      }
    },
    [hideKeyboard]
  );

  const onKeyPress = useCallback(
    (button: string, e?: MouseEvent) => {
      // prevent default mouse event so we don't lose focus on the input
      // as keys are pressed on the keyboard
      e?.preventDefault();

      if (activeInput.current) {
        const event = new KeyboardEvent('keydown', {
          bubbles: true,
          cancelable: true,
          key: button,
        });

        activeInput.current.dispatchEvent(event);
      }

      const enterKeyHint = activeInput.current?.enterKeyHint ?? '';

      if (button === '{enter}' && ['done', 'next'].includes(enterKeyHint)) {
        hideKeyboard();
      } else if (button.includes('{') && button.includes('}')) {
        handleLayoutChange(button);
      }
    },
    [hideKeyboard, handleLayoutChange]
  );

  const theme = useMemo(() => {
    const classes = ['hg-theme-default'];

    if (keyboardLayoutName === KeyboardLayout.NUMBER) {
      classes.push('numeric-theme');
    } else {
      classes.push('hg-theme-ios');
    }

    return classes.join(' ');
  }, [keyboardLayoutName]);

  return (
    <div
      className={KIOSK_KEYBOARD_CLASS_NAME}
      style={{
        visibility: isKeyboardVisible ? 'visible' : 'hidden',
        ...keyboardStyle,
      }}
    >
      <Keyboard
        keyboardRef={(r: KeyboardReactInterface) => (keyboardRef.current = r)}
        onChange={onChange}
        onKeyPress={onKeyPress}
        theme={theme}
        display={display}
        layout={KEYBOARD_LAYOUTS}
        layoutName={keyboardLayoutName}
      />
    </div>
  );
});

KioskKeyboard.displayName = 'KioskKeyboard';

const getInputsFromNodeList = (nodes: NodeList) => {
  const inputs: InputType[] = [];

  nodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if ('tagName' in node) {
        switch (node.tagName) {
          case 'INPUT':
            inputs.push(node as HTMLInputElement);
            break;
          case 'TEXTAREA':
            inputs.push(node as HTMLTextAreaElement);
            break;
        }
      }

      if ('querySelectorAll' in node) {
        const nestedInputs: HTMLInputElement[] =
          // @ts-ignore
          node.querySelectorAll('input');

        const nestedTextareas: HTMLTextAreaElement[] =
          // @ts-ignore
          node.querySelectorAll('textarea');

        inputs.push(...nestedInputs);
        inputs.push(...nestedTextareas);
      }
    }
  });

  return inputs;
};

const setInputValue = (input: InputType, value: string) => {
  // if this is a react input then we need to use the native setter
  // https://stackoverflow.com/questions/23892547/what-is-the-best-way-to-trigger-change-or-input-event-in-react-js
  // https://github.com/facebook/react/issues/11488
  if ('_valueTracker' in input && '__proto__' in input) {
    const nativeValueSetter = Object.getOwnPropertyDescriptor(
      input.__proto__,
      'value'
    )?.set;

    if (nativeValueSetter) {
      nativeValueSetter.call(input, value);
    }
  } else {
    input.value = value;
  }
};

const findCharacterChange = (modifiedVal: string, previousVal: string) => {
  const minLength = Math.min(modifiedVal.length, previousVal.length);

  // Compare the two strings character by character
  for (let i = 0; i < minLength; i++) {
    if (modifiedVal[i] !== previousVal[i]) {
      return modifiedVal[i];
    }
  }

  // If one string is longer, return the extra character(s)
  if (modifiedVal.length > previousVal.length) {
    return modifiedVal[previousVal.length];
  } else if (previousVal.length > modifiedVal.length) {
    return '';
  }

  return '';
};

export { KioskKeyboard };
